/**
 * The two live checkpoints — the IndexedDB {@link CheckpointStorePort}.
 *
 * Both stores are singletons keyed on one fixed literal, so the schema itself
 * makes two simultaneous active efforts unrepresentable. Single-effort authority
 * is a property of the store rather than a rule the engine has to remember.
 *
 * The interesting behaviour is the coalescing window (§4.5 rule 4). A coalesced
 * `save` does not write; it *holds* the snapshot and arms a timer, and the
 * snapshot becomes durable when the window elapses, when a semantic transition
 * arrives, or when {@link RecordCheckpointStore.flush} is called on a lifecycle
 * event. Three rules make five seconds a real bound rather than a hope:
 *
 *  1. **The timer is armed by the first coalesced write and never restarted.**
 *     Restarting it on each snapshot would let a steady 0.25 s tick postpone the
 *     write indefinitely, and the maximum loss window would be unbounded.
 *  2. **Clearing drops the pending copy.** A queued snapshot that survived a
 *     clear would resurrect a session the user already ended.
 *  3. **A flush that cannot be verified keeps the pending copy.** The next
 *     lifecycle flush retries it. Dropping it would turn one storage failure
 *     into silently lost training with nobody left holding the evidence.
 *
 * Time arrives through {@link TickerPort} rather than `setTimeout`, because a
 * store that reached for a browser global could not be tested at all and would
 * be the one place in this package that knows what a window is.
 */

import type {
  EffortCheckpoint,
  Milliseconds,
  OpenEffortCheckpoint,
  SchemaVersion,
  StoreReadResult,
  StructuredEffortCheckpoint,
  WriteOutcome,
} from "@ff/domain";
import { EFFORT_CHECKPOINT_SCHEMA_VERSION, STORE_IDS, schemaVersion } from "@ff/domain";
import type {
  Cancel,
  CheckpointStorePort,
  CheckpointWriteUrgency,
  EffortCheckpointKind,
  ObservedMutationGate,
  RecordStorePort,
  TickerPort,
} from "@ff/domain/ports";

import type { RecordDatabase } from "../kernel";
import { ACTIVE_RECORD_KEY, createRecordStore } from "../kernel";
import { CHECKPOINT_COALESCING_WINDOW } from "./coalescing";
import { decodeOpenEffortCheckpoint, decodeStructuredCheckpoint } from "./payload";

export const STRUCTURED_CHECKPOINT_VERSION: SchemaVersion = schemaVersion(
  EFFORT_CHECKPOINT_SCHEMA_VERSION,
);

/**
 * The open-effort checkpoint is at schema 1 while the structured one is at 2.
 * They are separate stores with separate histories, and `STORE_DESCRIPTORS` is
 * the authority for both — writing the structured version into this store would
 * make every existing open-effort record look like a future-schema refusal.
 */
export const OPEN_EFFORT_CHECKPOINT_VERSION: SchemaVersion = schemaVersion(1);

export function createStructuredCheckpointRecordStore(
  database: RecordDatabase,
): RecordStorePort<StructuredEffortCheckpoint> {
  return createRecordStore<StructuredEffortCheckpoint>({
    store: STORE_IDS.activeStructuredCheckpoint,
    schemaVersion: STRUCTURED_CHECKPOINT_VERSION,
    database,
    decodePayload: decodeStructuredCheckpoint,
  });
}

export function createOpenEffortCheckpointRecordStore(
  database: RecordDatabase,
): RecordStorePort<OpenEffortCheckpoint> {
  return createRecordStore<OpenEffortCheckpoint>({
    store: STORE_IDS.activeOpenEffortCheckpoint,
    schemaVersion: OPEN_EFFORT_CHECKPOINT_VERSION,
    database,
    decodePayload: decodeOpenEffortCheckpoint,
  });
}

export interface CheckpointStoreOptions {
  readonly structured: RecordStorePort<StructuredEffortCheckpoint>;
  readonly openEffort: RecordStorePort<OpenEffortCheckpoint>;
  readonly ticker: TickerPort;
  /** Overridable only so a test can watch the window; the product value is
   *  {@link CHECKPOINT_COALESCING_WINDOW} and it is a user-facing promise. */
  readonly window?: Milliseconds;
}

/**
 * The port plus the one fact a test needs that the port has no business
 * carrying: whether a snapshot is currently waiting on the window. A caller
 * that branched on this would be reimplementing the window outside the store.
 */
export interface CheckpointStore extends CheckpointStorePort {
  readonly pendingKind: EffortCheckpointKind | undefined;
}

export function checkpointStore(options: CheckpointStoreOptions): CheckpointStore {
  return new RecordCheckpointStore(options);
}

export function createCheckpointStore(
  database: RecordDatabase,
  ticker: TickerPort,
): CheckpointStore {
  return checkpointStore({
    structured: createStructuredCheckpointRecordStore(database),
    openEffort: createOpenEffortCheckpointRecordStore(database),
    ticker,
  });
}

class RecordCheckpointStore implements CheckpointStore {
  readonly #structured: RecordStorePort<StructuredEffortCheckpoint>;
  readonly #openEffort: RecordStorePort<OpenEffortCheckpoint>;
  readonly #ticker: TickerPort;
  readonly #window: Milliseconds;

  /** The snapshot the window is holding. Not durable, by design: five seconds is
   *  a real loss window, and pretending otherwise would hide it. */
  #pending: EffortCheckpoint | undefined;
  #armed: Cancel | undefined;

  constructor(options: CheckpointStoreOptions) {
    this.#structured = options.structured;
    this.#openEffort = options.openEffort;
    this.#ticker = options.ticker;
    this.#window = options.window ?? CHECKPOINT_COALESCING_WINDOW;
  }

  /** Which shape, if any, is waiting on the window. */
  get pendingKind(): EffortCheckpointKind | undefined {
    return this.#pending?.shape;
  }

  async readStructured(): Promise<StoreReadResult<StructuredEffortCheckpoint>> {
    return this.#structured.readRecord(ACTIVE_RECORD_KEY);
  }

  async readOpenEffort(): Promise<StoreReadResult<OpenEffortCheckpoint>> {
    return this.#openEffort.readRecord(ACTIVE_RECORD_KEY);
  }

  mutationGate(kind: EffortCheckpointKind): ObservedMutationGate {
    return kind === "structured"
      ? this.#structured.mutationGate()
      : this.#openEffort.mutationGate();
  }

  async save(
    checkpoint: EffortCheckpoint,
    urgency: CheckpointWriteUrgency,
  ): Promise<WriteOutcome> {
    if (urgency === "coalesced") {
      this.#pending = checkpoint;
      // Armed by the *first* coalesced write in the window and never restarted:
      // see rule 1 in the file comment.
      this.#armed ??= this.#ticker.after(this.#window, () => {
        void this.flush();
      });
      // Accepted, not yet durable. The engine has no branch for "queued", and a
      // coalesced snapshot that reported a failure the window has not reached
      // yet would be a failure about a write that never happened.
      return { status: "verified" };
    }

    // A superseded snapshot must never land after the newer one it was replaced
    // by, so an immediate write of the same shape consumes the pending copy.
    this.#discardPending(checkpoint.shape);
    return this.#write(checkpoint);
  }

  async clear(kind: EffortCheckpointKind): Promise<WriteOutcome> {
    this.#discardPending(kind);
    const store = kind === "structured" ? this.#structured : this.#openEffort;
    return store.commit([{ kind: "remove", key: ACTIVE_RECORD_KEY }]);
  }

  async flush(): Promise<WriteOutcome> {
    const pending = this.#pending;
    // A flush with nothing pending is a verified no-op, not a failure. Lifecycle
    // events fire far more often than coalesced writes.
    if (pending === undefined) {
      this.#disarm();
      return { status: "verified" };
    }

    this.#pending = undefined;
    this.#disarm();
    const outcome = await this.#write(pending);
    if (outcome.status !== "verified") {
      // Rule 3: hold it for the next flush rather than dropping the only copy of
      // what the user just did. The timer stays disarmed — retrying on a timer
      // would spin against a store that is refusing for a reason a timer cannot
      // fix; the next lifecycle event is when it is worth trying again.
      this.#pending = pending;
    }
    return outcome;
  }

  /* --- internals --------------------------------------------------------- */

  #discardPending(kind: EffortCheckpointKind): void {
    if (this.#pending?.shape !== kind) return;
    this.#pending = undefined;
    this.#disarm();
  }

  #disarm(): void {
    const cancel = this.#armed;
    this.#armed = undefined;
    // `Cancel` is defined as safe to call more than once, so teardown paths do
    // not have to track whether the timer already fired.
    cancel?.();
  }

  #write(checkpoint: EffortCheckpoint): Promise<WriteOutcome> {
    // The union discriminates which store is written, so no caller can address
    // the wrong one by passing the wrong string.
    return checkpoint.shape === "structured"
      ? this.#structured.commit([
          { kind: "put", key: ACTIVE_RECORD_KEY, value: checkpoint },
        ])
      : this.#openEffort.commit([
          { kind: "put", key: ACTIVE_RECORD_KEY, value: checkpoint },
        ]);
  }
}
